import { Link } from "react-router-dom";
import {
  ArrowRight,
  Car,
  Home as HomeIcon,
  HeartPulse,
  Stethoscope,
  Building2,
  Truck,
  Zap,
  ShieldCheck,
  Scale,
  UserCheck,
  TrendingDown,
  Smartphone,
  Bell,
  FileText,
  Headphones,
  Instagram,
  Facebook,
  ArrowUpRight,
  Phone,
  Mail,
} from "lucide-react";
import { BRAND, SERVICES, ADVANTAGES } from "../data/brand";
import CompaniesSlider from "../components/CompaniesSlider";

const ICONS = {
  Car,
  Home: HomeIcon,
  HeartPulse,
  Stethoscope,
  Building2,
  Truck,
  Zap,
  ShieldCheck,
  Scale,
  UserCheck,
  TrendingDown,
};

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[88vh]">
          {/* Left content */}
          <div className="lg:col-span-6 flex items-center bg-white relative">
            <div className="container-90 w-full py-20 lg:py-0 lg:pr-16 lg:pl-[calc((100vw-1280px)/2+24px)]">
              <div className="fade-up max-w-2xl">
                <div className="mb-8">
                  <span className="overline">Broker assicurativo · Frosinone</span>
                </div>

                <h1 className="font-heading text-4xl sm:text-5xl lg:text-[4.2rem] font-extrabold leading-[1.02] tracking-tight text-slate-900">
                  La polizza giusta
                  <br />
                  non si sceglie:
                  <br />
                  <span className="text-[color:var(--brand-blue)]">si costruisce</span>
                  <span className="text-[color:var(--brand-yellow)]">.</span>
                </h1>

                <p className="mt-8 text-lg text-slate-600 leading-relaxed max-w-xl">
                  Siamo un broker indipendente a Frosinone. Confrontiamo le migliori
                  compagnie del mercato per offrirti una consulenza assicurativa su misura,
                  trasparente e senza vincoli.
                </p>

                <div className="mt-10 flex flex-col sm:flex-row gap-4">
                  <a
                    href={`tel:${BRAND.phoneRaw}`}
                    data-testid="hero-consulenza-btn"
                    className="btn-primary"
                  >
                    Richiedi consulenza <ArrowRight className="w-4 h-4" />
                  </a>
                  <a
                    href={`mailto:${BRAND.email}?subject=Richiesta preventivo gratuito`}
                    data-testid="hero-preventivo-btn"
                    className="btn-outline"
                  >
                    Preventivo gratuito
                  </a>
                </div>

                {/* Trust strip */}
                <div className="mt-16 grid grid-cols-3 gap-8 border-t border-slate-200 pt-8">
                  <div className="border-t-2 border-[color:var(--brand-yellow)] pt-4 -mt-[10px]">
                    <div className="font-heading text-3xl font-extrabold text-slate-900">20+</div>
                    <div className="text-xs uppercase tracking-wider text-slate-500 mt-1">
                      Compagnie confrontate
                    </div>
                  </div>
                  <div className="border-t-2 border-[color:var(--brand-yellow)] pt-4 -mt-[10px]">
                    <div className="font-heading text-3xl font-extrabold text-slate-900">100%</div>
                    <div className="text-xs uppercase tracking-wider text-slate-500 mt-1">
                      Indipendenti
                    </div>
                  </div>
                  <div className="border-t-2 border-[color:var(--brand-yellow)] pt-4 -mt-[10px]">
                    <div className="font-heading text-3xl font-extrabold text-slate-900">1:1</div>
                    <div className="text-xs uppercase tracking-wider text-slate-500 mt-1">
                      Consulenza dedicata
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right image */}
          <div className="lg:col-span-6 relative min-h-[420px] lg:min-h-full bg-white border-l border-slate-100 overflow-hidden">
            <img
              src="https://customer-assets.emergentagent.com/job_brokers-consultancy/artifacts/eb6tn2us_ChatGPT%20Image%209%20mag%202026%2C%2010_35_26.png"
              alt="Soluzioni 90 Brokers: Auto, Aziende, Energia, Casa, Vita"
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
          </div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section id="servizi" className="section bg-white">
        <div className="container-90">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
            <div className="max-w-2xl">
              <div className="overline mb-4">Le nostre aree di tutela</div>
              <h2 className="font-heading text-3xl sm:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
                Sette aree, una sola filosofia:
                <br />
                <span className="text-[color:var(--brand-blue)]">la tua sicurezza</span>
                <span className="text-[color:var(--brand-yellow)]">.</span>
              </h2>
            </div>
            <Link
              to="/servizi"
              data-testid="services-see-all-link"
              className="btn-ghost inline-flex items-center gap-2 self-start lg:self-auto"
            >
              Esplora tutti i servizi <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {SERVICES.map((s) => {
              const Icon = ICONS[s.icon] || ShieldCheck;
              return (
                <Link
                  key={s.id}
                  to={`/servizi#${s.id}`}
                  data-testid={`service-${s.id}-card`}
                  className="service-card group flex flex-col"
                >
                  <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
                    <img
                      src={s.image}
                      alt={s.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 left-4 w-10 h-10 bg-white/95 flex items-center justify-center">
                      <Icon className={`w-4 h-4 ${s.energiaForm ? "text-emerald-700" : "text-[color:var(--brand-blue)]"}`} />
                    </div>
                    {s.energiaForm && (
                      <div className="absolute top-4 right-4 px-3 py-1 bg-emerald-600 text-white text-[0.6rem] uppercase tracking-[0.16em] font-bold">
                        Risparmia
                      </div>
                    )}
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">
                      {s.title}
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed mb-4">
                      {s.short}
                    </p>
                    <span className={`mt-auto inline-flex items-center gap-2 font-semibold text-sm ${s.energiaForm ? "text-emerald-700" : "text-[color:var(--brand-blue)]"}`}>
                      Scopri <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* COMPANIES SLIDER */}
      <CompaniesSlider />

      {/* COME FUNZIONA / ADVANTAGES */}
      <section className="section bg-slate-50 border-y border-slate-200">
        <div className="container-90">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            <div className="lg:col-span-4">
              <div className="overline mb-4">Perché un broker</div>
              <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 leading-[1.08] mb-6">
                Non vendiamo polizze.
                <br />
                Costruiamo
                <br />
                <span className="text-[color:var(--brand-blue)]">soluzioni.</span>
              </h2>
              <p className="text-slate-600 leading-relaxed mb-8">
                La differenza tra un agente e un broker è semplice: il broker lavora per te,
                non per la compagnia. Il risultato? Coperture migliori a costi ottimizzati.
              </p>
              <Link
                to="/come-funziona"
                data-testid="how-it-works-link"
                className="btn-outline"
              >
                Come funziona
              </Link>
            </div>

            <div className="lg:col-span-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 grid-border bg-white">
                {ADVANTAGES.map((a) => {
                  const Icon = ICONS[a.icon] || ShieldCheck;
                  return (
                    <div
                      key={a.id}
                      data-testid={`advantage-${a.id}-card`}
                      className="grid-border-cell last:border-b-0 sm:[&:nth-last-child(-n+2)]:border-b-0 sm:nth-[even]:border-r-0"
                      style={{}}
                    >
                      <Icon className="w-8 h-8 text-[color:var(--brand-blue)] mb-5" strokeWidth={1.5} />
                      <h3 className="font-heading text-xl font-bold text-slate-900 mb-3">
                        {a.title}
                      </h3>
                      <p className="text-slate-600 text-sm leading-relaxed">{a.body}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SOCIAL SECTION */}
      <section className="section bg-white">
        <div className="container-90">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <div className="overline mb-4">Community</div>
              <h2 className="font-heading text-3xl sm:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
                Seguici sui social
                <br />
                <span className="text-[color:var(--brand-blue)]">per aggiornamenti e consigli.</span>
              </h2>
            </div>
            <div className="flex gap-3">
              {BRAND.social.map((s, i) => (
                <a
                  key={i}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`social-follow-${i}-link`}
                  className="w-12 h-12 flex items-center justify-center bg-slate-900 text-white hover:bg-[color:var(--brand-blue)] transition-colors"
                  aria-label={s.handle}
                >
                  {s.network === "Instagram" ? (
                    <Instagram className="w-5 h-5" />
                  ) : (
                    <Facebook className="w-5 h-5" />
                  )}
                </a>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                img: "https://images.unsplash.com/photo-1582407947304-fd86f028f716?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
                label: "Consigli casa",
                caption: "Come scegliere la polizza casa giusta",
              },
              {
                img: "https://images.unsplash.com/photo-1449824913935-59a10b8d2000?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
                label: "RC Auto",
                caption: "3 errori da evitare nel rinnovo auto",
              },
              {
                img: "https://images.unsplash.com/photo-1559526324-4b87b5e36e44?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
                label: "Patrimonio",
                caption: "Proteggere ciò che conta davvero",
              },
            ].map((p, i) => (
              <a
                key={i}
                href={BRAND.social[0].url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`social-post-${i}-link`}
                className="group relative aspect-square overflow-hidden bg-slate-200"
              >
                <img
                  src={p.img}
                  alt={p.caption}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
                <div className="absolute top-5 left-5 flex items-center gap-2">
                  <Instagram className="w-4 h-4 text-white" />
                  <span className="overline text-white/90">{p.label}</span>
                </div>
                <div className="absolute bottom-6 left-6 right-6">
                  <p className="font-heading text-white text-lg font-bold leading-snug">
                    {p.caption}
                  </p>
                  <div className="mt-3 flex items-center gap-2 text-white/80 text-xs">
                    <ArrowUpRight className="w-4 h-4" />
                    <span>@90brokers.assicurazioni</span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* APP PROMO — hero-scale prominent section */}
      <section className="relative bg-[#060a17] text-white overflow-hidden">
        {/* Ambient blue glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 -left-40 w-[600px] h-[600px] rounded-full bg-[color:var(--brand-blue)] opacity-30 blur-[140px]" />
          <div className="absolute bottom-0 right-0 w-[700px] h-[700px] rounded-full bg-[color:var(--brand-blue-light)] opacity-20 blur-[180px]" />
        </div>
        {/* Grid pattern overlay */}
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />

        <div className="container-90 relative py-28 lg:py-36">
          {/* Top badge + heading block */}
          <div className="max-w-4xl mb-20">
            <div className="inline-flex items-center gap-3 px-5 py-2.5 border border-[color:var(--brand-yellow)]/40 bg-[color:var(--brand-yellow)]/10 text-xs uppercase tracking-[0.22em] font-bold text-[color:var(--brand-yellow)] mb-10">
              <span className="relative flex w-2 h-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[color:var(--brand-yellow)] opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[color:var(--brand-yellow)]" />
              </span>
              App 90 Brokers · In arrivo
            </div>
            <h2 className="font-heading text-5xl sm:text-6xl lg:text-[5.5rem] font-extrabold tracking-tight leading-[0.98] mb-8">
              La tua assicurazione,
              <br />
              <span className="text-[color:var(--brand-blue-light)]">sempre in tasca.</span>
            </h2>
            <p className="text-xl text-slate-300 leading-relaxed max-w-2xl">
              Stiamo costruendo l'app ufficiale di 90 Brokers: uno strumento pensato per darti
              controllo immediato su polizze, scadenze e assistenza — ovunque tu sia.
            </p>
          </div>

          {/* Feature grid — large */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border border-white/10 mb-16">
            {[
              {
                icon: FileText,
                num: "01",
                t: "Gestione polizze",
                d: "Tutti i tuoi contratti consultabili in un solo posto, sempre aggiornati.",
              },
              {
                icon: Headphones,
                num: "02",
                t: "Assistenza rapida",
                d: "Un tap per aprire un sinistro o parlare direttamente con un consulente.",
              },
              {
                icon: Bell,
                num: "03",
                t: "Notifiche smart",
                d: "Scadenze, rinnovi e promemoria ricordati al momento giusto.",
              },
              {
                icon: Smartphone,
                num: "04",
                t: "Interfaccia semplice",
                d: "Un design pulito, pensato per essere utilizzato da chiunque.",
              },
            ].map((f, i) => {
              const Icon = f.icon;
              return (
                <div
                  key={i}
                  data-testid={`app-feature-${i}-card`}
                  className={`p-10 lg:p-12 bg-white/[0.02] hover:bg-white/[0.05] transition-colors ${
                    i < 3 ? "md:border-r border-white/10" : ""
                  } ${i < 2 ? "md:border-b lg:border-b-0 border-white/10" : ""}`}
                >
                  <div className="flex items-start justify-between mb-8">
                    <Icon className="w-9 h-9 text-[color:var(--brand-blue-light)]" strokeWidth={1.4} />
                    <span className="font-heading text-3xl font-extrabold text-[color:var(--brand-yellow)]/40">
                      {f.num}
                    </span>
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-white mb-3">{f.t}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{f.d}</p>
                </div>
              );
            })}
          </div>

          {/* Bottom bar: CTA */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8 pt-10 border-t border-white/10">
            <div>
              <div className="overline text-[color:var(--brand-blue-light)]/90 mb-3">
                Lancio ufficiale
              </div>
              <div className="font-heading text-3xl lg:text-4xl font-extrabold text-white leading-tight">
                Prossimamente disponibile su
                <br />
                <span className="text-[color:var(--brand-blue-light)]">App Store e Google Play</span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <div
                data-testid="app-store-placeholder"
                className="flex items-center gap-3 px-6 py-4 bg-white/5 border border-white/15 hover:border-[color:var(--brand-yellow)]/60 transition-colors"
              >
                <Smartphone className="w-5 h-5 text-[color:var(--brand-yellow)]" />
                <div className="text-left">
                  <div className="text-[0.65rem] uppercase tracking-widest text-slate-400">
                    Disponibile su
                  </div>
                  <div className="font-heading font-bold text-white">App Store</div>
                </div>
              </div>
              <div
                data-testid="play-store-placeholder"
                className="flex items-center gap-3 px-6 py-4 bg-white/5 border border-white/15 hover:border-[color:var(--brand-yellow)]/60 transition-colors"
              >
                <Smartphone className="w-5 h-5 text-[color:var(--brand-yellow)]" />
                <div className="text-left">
                  <div className="text-[0.65rem] uppercase tracking-widest text-slate-400">
                    Disponibile su
                  </div>
                  <div className="font-heading font-bold text-white">Google Play</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA — dark band right before the footer */}
      <section className="relative bg-[#0a0f1f] text-white overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 right-0 w-[600px] h-[600px] rounded-full bg-[color:var(--brand-blue)] opacity-20 blur-[160px]" />
        </div>
        <div className="container-90 relative section">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="overline text-[color:var(--brand-blue-light)] mb-6">
                Iniziamo a lavorare insieme
              </div>
              <h2 className="font-heading text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] mb-8">
                Una consulenza gratuita
                <br />
                <span className="text-[color:var(--brand-blue-light)]">può cambiare la tua tutela.</span>
              </h2>
              <p className="text-lg text-slate-300 leading-relaxed mb-10 max-w-2xl">
                Parla con un nostro consulente: analizzeremo le tue coperture attuali e ti
                mostreremo dove puoi migliorare garanzie e risparmiare.
              </p>
            </div>

            <div className="lg:col-span-5 lg:border-l lg:border-white/10 lg:pl-12 space-y-4">
              <a
                href={`tel:${BRAND.phoneRaw}`}
                data-testid="final-cta-phone-btn"
                className="flex items-center gap-4 p-5 bg-white/5 border border-white/10 hover:bg-white/10 hover:border-[color:var(--brand-yellow)]/50 transition-all group"
              >
                <div className="w-11 h-11 bg-[color:var(--brand-yellow)] flex items-center justify-center">
                  <Phone className="w-5 h-5 text-[#0a0f1f]" />
                </div>
                <div className="flex-1">
                  <div className="text-[0.65rem] uppercase tracking-[0.2em] font-bold text-white/60">
                    Chiamaci
                  </div>
                  <div className="font-heading text-lg font-bold text-white">{BRAND.phone}</div>
                </div>
                <ArrowRight className="w-4 h-4 text-[color:var(--brand-yellow)] group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href={`mailto:${BRAND.email}`}
                data-testid="final-cta-email-btn"
                className="flex items-center gap-4 p-5 bg-white/5 border border-white/10 hover:bg-white/10 hover:border-[color:var(--brand-yellow)]/50 transition-all group"
              >
                <div className="w-11 h-11 bg-[color:var(--brand-yellow)] flex items-center justify-center">
                  <Mail className="w-5 h-5 text-[#0a0f1f]" />
                </div>
                <div className="flex-1">
                  <div className="text-[0.65rem] uppercase tracking-[0.2em] font-bold text-white/60">
                    Scrivici via email
                  </div>
                  <div className="font-heading text-base font-bold text-white break-all">
                    {BRAND.email}
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-[color:var(--brand-yellow)] group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
